/**
 * SoleadLab 2.0 — cross-inspection observation helpers.
 *
 * A fault (indication) can be seen across several inspections. Its history is
 * `properties.observations[]` (one per inspection). For backward-compat the
 * top-level image/captureDate/thermalData mirror the LATEST observation, and a
 * fault with no explicit array is treated as a single derived observation —
 * so existing single-date indications "just work" with no data migration.
 */

import type { Indication, IndicationObservation } from '../stores/projectStore';
import { captureDay } from './captureDate';

/**
 * The fault's observation history, newest LAST. Returns the explicit
 * `observations[]` when present; otherwise derives a single observation from the
 * indication's current top-level fields (lazy, non-mutating).
 */
export function deriveObservations(ind: Indication): IndicationObservation[] {
  const p = ind.properties;
  if (p.observations && p.observations.length > 0) return p.observations;
  return [{
    date: p.captureDate,
    sourcePhotoId: p.sourcePhotoId,
    image: p.image,
    visualImage: p.visualImage,
    zoomImage: p.zoomImage,
    thermalData: p.thermalData,
  }];
}

/** Whether a fault has been observed in more than one inspection. */
export function hasHistory(ind: Indication): boolean {
  return deriveObservations(ind).length > 1;
}

/**
 * Whether `obs` is the fault's LATEST observation. A ≤1-length history is
 * inherently the latest — derived single observations are fabricated fresh on
 * every deriveObservations() call, so a caller holding one can never match by
 * reference. Reference equality is only meaningful for the explicit array.
 */
export function isLatestObservation(ind: Indication, obs: IndicationObservation | null): boolean {
  const list = deriveObservations(ind);
  return list.length <= 1 || obs === list[list.length - 1];
}

/**
 * Every distinct inspection DAY (captureDay-normalized) this fault was observed on.
 * A persistent fault seen across several inspections returns all of them, so
 * date filtering / per-date counts credit it on EACH date — not just its latest.
 * `[]` when the fault carries no dates at all (⇒ treat as always-visible).
 */
export function observationDays(ind: Indication): string[] {
  const days = new Set<string>();
  for (const o of deriveObservations(ind)) {
    if (o.date) days.add(captureDay(o.date));
  }
  return [...days];
}

/**
 * Date-visibility predicate: is this fault observed on ANY currently-visible day?
 * Dateless faults are always visible (mirrors the legacy "undated ⇒ shown" rule).
 */
export function observedOnVisibleDay(ind: Indication, visibleDays: Set<string>): boolean {
  const days = observationDays(ind);
  if (days.length === 0) return true;
  return days.some((d) => visibleDays.has(d));
}

/**
 * The observation to DISPLAY for a given view — the single source of truth shared
 * by every popup (map preview, edit form, classify mini-map card):
 *  - 'current' → the latest observation (current state),
 *  - 'asFound' → the latest observation on a currently-visible day (falls back to
 *    latest when the fault wasn't observed on any visible day).
 * Returns null only when the fault carries no observations at all. Callers read
 * imagery/thermal STRICTLY from the returned observation — never a top-level
 * fallback — so a year with no analysis shows no ROI (no cross-year carryover).
 */
export function activeObservation(
  ind: Indication,
  viewMode: 'asFound' | 'current',
  visibleDays: Set<string>,
): IndicationObservation | null {
  const obs = deriveObservations(ind);
  if (obs.length === 0) return null;
  if (viewMode === 'current') return obs[obs.length - 1];
  const inView = obs.filter((o) => o.date && visibleDays.has(captureDay(o.date)));
  return inView.length ? inView[inView.length - 1] : obs[obs.length - 1];
}
